<template>
  <div>
    <div class="chart-filters-button d-flex justify-content-center align-items-center"
         :class="{'show': chart.showFilters}" @click="toggleFilters">
      <font-awesome-icon :icon="chart.showFilters ? 'chevron-right' : 'chevron-left'"></font-awesome-icon>
    </div>
    <div class="chart-filters-tab py-3 d-flex flex-column align-items-center justify-content-between"
         :class="{'show': chart.showFilters}">
      <div>
        <div class="d-flex justify-content-between">
          <div class="input-wrapper mb-2 justify-content-end" style="width: 93px">
            <el-select
                collapse-tags
                placeholder="Best"
                v-model="chart.filters.results.order"
            >
              <el-option
                  v-for="value in [{name: 'Best', value: 'best'}, {name: 'Worst', value: 'worst'}]"
                  :key="value.value" :label="value.name" :value="value.value">
              </el-option>
            </el-select>
          </div>
          <div class="input-wrapper mb-2" style="width: 93px">
            <label>Attributes #:</label>
            <el-input placeholder="Value"
                      v-model="chart.filters.results.count"
                      clearable></el-input>
          </div>
        </div>
        <div class="input-wrapper">
          <label>Attribute Type:</label>
          <el-select
              collapse-tags
              placeholder="Select values"
              v-model="chart.filters.family"
          >
            <el-option
                v-for="value in attributeTypes"
                :key="value.value" :label="value.name" :value="value.value">
            </el-option>
          </el-select>
        </div>
        <div class="input-wrapper mt-2">
          <label>Aggregation:</label>
          <el-select
              collapse-tags
              placeholder="Select values"
              v-model="chart.filters.aggregation"
          >
            <el-option
                v-for="value in aggregations"
                :key="value.value" :label="value.name" :value="value.value">
            </el-option>
          </el-select>
        </div>
        <button class="search-button btn btn-block mt-3" @click="applyFilters" type="button" :disabled="!(chart.filters.aggregation && chart.filters.family)">
          Run
        </button>
      </div>
      <img src="../../../assets/images/icons/excel-export-H.svg" class="dwn-button" @click="download('attributes')"/>
    </div>
    <div class="chart-filters-overlay" :class="{'show': chart.showFilters, hide: chart.hideOverlay}"
         @click="toggleFilters()"></div>
    <div v-if="rawData && rawData.length">
      <div class="table-container">
        <div class="d-flex justify-content-around table-header">
          <div class="column-header" style="width: 20%;" @click="changeSort('attribute')">
            <span class="mr-1">Attributes</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'attribute' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'attribute' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div class="column-header" style="width: 45%;">
            <span class="mr-1">Top 2 Box / Bottom 2 Box</span>
          </div>
          <div class="column-header" style="width: 10%;" @click="changeSort('mean')">
            <span class="mr-1">Mean</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'mean' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'mean' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div class="column-header" style="width: 10%;" @click="changeSort('top_box')">
            <span class="mr-1">Top Box</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'top_box' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'top_box' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
          <div class="column-header" style="width: 10%;" @click="changeSort('bottom_box')">
            <span class="mr-1">Bot. Box</span>
            <div>
              <font-awesome-icon :icon="'caret-up'"
                                 :color="sortBy.key === 'bottom_box' && sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
              <font-awesome-icon :icon="'caret-down'"
                                 :color="sortBy.key === 'bottom_box' && !sortBy.desc ? '#63c2de' : '#d1d4d7'"></font-awesome-icon>
            </div>
          </div>
        </div>
        <div class="scroll-wrapper" style="height: calc(100% - 50px);">

          <RecycleScroller
              :items="filteredData"
              :item-size="50"
              class="scroller"
              :emit-update="true"
          >
            <template v-slot="{ item }">
              <div class="data d-flex flex-column justify-content-center align-items-center">
                <div class="main-data" style="padding: 0 10px;">
                  <div class="column-body" style="width: 20%;">
                    {{ formatValue(item.attribute) }}
                  </div>
                  <div class="column-body" style="width: 45%;">
                    <div class="box-num" style="margin-right: 6px; width: 31px;">
                      {{ formatValue(item.top_2_box) }}
                    </div>
                    <div class="d-flex" style="width: 60%">
                      <div class="tb-box" v-for="box in calcBoxValues(item.top_2_box, item.bottom_2_box)"
                           :key="box.name"
                           v-b-tooltip.hover.html="`${box.name}: ${box.value}%`"
                           :style="`
                           background-color: ${box.color};
                           margin: 0.5px;
                           border: solid 0.1px #4b649b;
                           width: ${box.value}%;
                           ${box.value === 0 ? 'display: none;' : ''}
                           `">
                        &nbsp;
                      </div>
                    </div>
                    <div class="box-num" style="margin-left: 6px; width: 31px;">
                      {{ formatValue(item.bottom_2_box) }}
                    </div>
                  </div>
                  <div class="column-body" style="width: 10%;">
                    {{formatValue(item.mean)}}
                  </div>
                  <div class="column-body" style="width: 10%;">
                    {{formatValue(item.top_box, true)}}
                  </div>
                  <div class="column-body" style="width: 10%;">
                    {{formatValue(item.bottom_box, true)}}
                  </div>
                </div>
              </div>
            </template>
          </RecycleScroller>
        </div>
      </div>
    </div>
    <div v-else-if="ready.attributes" class="no-data d-flex justify-content-center align-items-center">
      <i v-if="searchExecuted">No data available for provided filters.</i>
      <i v-else>Select filters in order to see performance data.</i>
    </div>
  </div>
</template>

<script>
import 'vue-virtual-scroller/dist/vue-virtual-scroller.css'
import { mapGetters } from 'vuex'
import studyGetters from '../../../store/modules/studies/getterTypes'

export default {
  name: 'TopAttributesTable',
  props: {
    data: {
      type: Array,
      required: false
    },
    fragrances: {
      type: Array,
      default: null
    },
    ready: Object,
    filterFields: Object,
    searchData: Function,
    download: Function,
    parseSearchData: Function
  },
  data () {
    return {
      searchExecuted: false,
      resizeToggle: false,
      sortBy: {
        key: 'attribute',
        desc: true
      },
      chart: {
        data: {
          labels: ['Africa', 'Asia', 'Europe', 'Latin America', 'North America'],
          datasets: [
            {
              label: 'Metrics',
              backgroundColor: '#3e95cd',
              data: [8, 7, 4, 3, 3]
            }
          ]
        },
        options: {
          scales: {
            xAxes: [{
              gridLines: {
                display: false
              }
            }],
            yAxes: [{
              ticks: {
                min: 0
              }
            }]
          },
          barValueSpacing: 20,
          responsive: true,
          maintainAspectRatio: false
        },
        showFilters: true,
        hideOverlay: false,
        filters: {
          aggregation: '',
          family: '',
          results: {
            order: 'best',
            count: 10
          }
        }
      }
    }
  },
  computed: {
    ...mapGetters({
      filters: studyGetters.GET_SEARCH_FILTERS,
      rawData: studyGetters.GET_ANALYTICS_ATTRIBUTES
    }),
    filteredData () {
      const arrayCopy = this.formatData()
      const sortedArray = arrayCopy.sort((a, b) => {
        if (a[this.sortBy.key] < b[this.sortBy.key]) {
          return -1
        }
        if (a[this.sortBy.key] > b[this.sortBy.key]) {
          return 1
        }
        return 0
      })
      return this.sortBy.desc ? sortedArray : sortedArray.reverse()
    },
    attributeTypes () {
      return Object.keys(this.filterFields) ? this.filterFields.attributes_metadata.attribute_type : []
    },
    aggregations () {
      return Object.keys(this.filterFields) ? this.filterFields.attributes_metadata.aggregations : []
    }
  },
  methods: {
    formatData () {
      const formattedArray = []
      this.rawData.forEach((el, idx) => {
        formattedArray.push({
          id: idx,
          ...el
        })
      })
      return formattedArray
    },
    formatValue (value, isPercentage) {
      if (typeof value === 'string') {
        return value && value.charAt(0).toUpperCase() + value.slice(1)
      } else {
        return value ? isPercentage ? Math.round(value) + '%' : value.toFixed(2) : '-'
      }
    },
    calcBoxValues (top, bottom) {
      const topp = Math.round(top)
      const bott = Math.round(bottom)
      const mid = 100 - topp - bott

      return [
        { name: 'Top 2 Box', color: '#182852', value: topp, label: 'T2B' },
        { name: 'Neither/Nor', color: '#6e87be', value: mid },
        { name: 'Bottom 2 Box', color: '#d2dcf0', value: bott, label: 'B2B' }
      ]
    },
    toggleFilters () {
      if (!this.chart.hideOverlay) {
        setTimeout(() => this.chart.hideOverlay = true, 200)
      } else {
        this.chart.hideOverlay = false
      }
      setTimeout(() => this.chart.showFilters = !this.chart.showFilters, 0)
    },
    clearToggledRows () {
      this.openedRows = []
    },
    applyFilters () {
      this.ready.attributes = false
      this.searchData(this.parseSearchData(this.filters), this.chart.filters, 'attributes')
      this.searchExecuted = true
    },
    changeSort (newKey) {
      if (this.sortBy.key === newKey) {
        const test = !this.sortBy.desc
        this.$set(this.sortBy, 'desc', test)
      } else {
        this.$set(this.sortBy, 'key', newKey)
        this.$set(this.sortBy, 'desc', true)
      }
    }
  },
  watch: {
    'chart.filters.family': {
      handler (newVal, oldVal) {
        if (newVal !== oldVal) {
          this.chart.filters.aggregation = ''
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "../../../assets/scss/variables.scss";
.table-container {
  overflow: scroll;
  position: relative;
  max-height: calc(55vh - 4rem);
  min-height: 523px;
}

.main-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  height: 50px;
  width: 100%;
  cursor: pointer;
}

.additional-data {
  display: flex;
  justify-content: space-around;
  align-items: center;
  overflow: hidden;
  width: 98%;
  cursor: pointer;
}

.data {
  border-bottom: 1px solid #d1d4d7;
  transition: 0.1s background-color linear;
}

.data:hover {
  background-color: #f6f6f6;
}

.additional-data-hide {
  height: 0;
}

.additional-data-show {
  height: 50px;
  border-top: 1px dashed #d1d4d7
}

.scroller {
  height: 100%;
}

.column-header {
  cursor: pointer;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 12px 0;
  transition: 0.1s background-color linear;
}

.column-header:hover {
  background-color: #f6f6f6;
}

.column-body {
  display: flex;
  justify-content: center;
  align-items: center;
  text-align: center;
}

.no-data {
  max-height: calc(55vh - 4rem);
  min-height: 523px;
  font-size: 18px;
}

.table-header {
  z-index: 3;
  position: sticky;
  top: 0;
  background-color: white;
  border-bottom: 1px solid #d1d4d7;
}

.no-hover:hover {
  background-color: unset;
}

.chart-filters-tab {
  overflow: hidden;
  margin-top: -1rem;
  z-index: 5;
  position: absolute;
  right: 0;
  height: 100%;
  width: 0;
  background-color: white;
  border-left: 0;
  transition: width 0.2s ease-in, border-left 0.2s ease-in;
}

.chart-filters-tab.show {
  width: 280px;
  border-left: 1px solid #d1d4d7;
}

.chart-filters-overlay {
  z-index: 4;
  width: 100%;
  height: 100%;
  position: absolute;
  margin-top: -1rem;
  margin-left: -1rem;
  background-color: rgba(0, 0, 0, 0);
  transition: background-color 0.2s ease-in;
}

.chart-filters-overlay.show {
  background-color: rgba(0, 0, 0, 0.1);
}

.chart-filters-overlay.hide {
  display: none;
}

.chart-filters-button {
  cursor: pointer;
  border-left: 1px solid #d1d4d7;
  border-bottom: 1px solid #d1d4d7;
  border-top: 1px solid #d1d4d7;
  z-index: 5;
  position: absolute;
  background-color: white;
  right: 0;
  width: 40px;
  height: 40px;
  bottom: 15px;
  transition: right 0.2s ease-in;
}

.chart-filters-button.show {
  right: 280px;
}

.input-wrapper {
  display: flex;
  flex-direction: column;
  justify-content: center;
  width: 100%;
}

.input-wrapper input {
  width: 100%;
}

.input-wrapper .el-select {
  width: 100%;
}

.input-wrapper label {
  color: #202124;
  margin-bottom: 2px;
  margin-left: 2px;
  font-family: 'Source Sans Pro', sans-serif;
  font-size: 13px;
  line-height: 20px;
  font-weight: 600;
}

.search-button {
  overflow: hidden;
  white-space: nowrap;
  border-radius: 5px;
}

.box-num {
  font-size: x-small;
}

.tb-box {
  font-size: x-small;
  color: white;
}

.search-button {
  width: 230px;
  height: 48px;
  background-color: #300296;
  font-family: 'Open Sans', sans-serif;
  font-size: 16px;
  font-weight: 800;
  line-height: 30px;
  letter-spacing: 0;
  border-radius: 4px;
  color: white;
  text-transform: uppercase;
  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: #9256D9;
  }
}

.download-button {
  height: 48px;
  background-color: $celery-500;
  border-radius: 4px;
  font-family: 'Open Sans', sans-serif;
  font-weight: 900;
  font-size: 16px;
  line-height: 30px;
  letter-spacing: 0;
  text-transform: uppercase;
  color: white;

  transition: background-color 0.2s linear;

  &:hover {
    color: white;
    background-color: $celery-700;
  }
}
</style>
